import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import { CompleteExpressQueryKiosk_company_categories } from '~Scenes/SelfServiceKiosks/Components/useExpressQuery/__queries__';
import { useIsLandscape } from '~Scenes/SelfServiceKiosks/Hooks/useIsLandscape';

export const menuHeight = 184;
export const landscapeMenuHeight = 128;

const CategoriesList = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  background: rgb(248, 248, 251);
  position: relative;
`;

const CategoriesListInner = styled.div`
  display: flex;
  align-items: center;
  overflow: auto;
  height: ${menuHeight}px;

  ::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  @media (orientation: landscape) {
    height: ${landscapeMenuHeight}px;
  }
`;

const CategoryGradient = styled.div<{ right?: boolean }>`
  position: absolute;
  left: ${({ right }) => (right ? 'initial' : '0px')};
  right: ${({ right }) => (right ? '0px' : 'initial')};
  top: 0;
  height: 184px;
  width: 204px;
  background: ${({ right }) =>
    right
      ? 'linear-gradient(-90deg, rgb(245, 245, 248) 0%, rgba(245, 245, 248, 0) 100%)'
      : 'linear-gradient(-90deg, rgba(245, 245, 248, 0) 0%, rgb(245, 245, 248) 100%)'};
  pointer-events: none;

  @media (orientation: landscape) {
    height: ${landscapeMenuHeight}px;
    width: 114px;
  }
`;

const CategoryListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 136px;
  min-width: calc(100vw / 5);
  text-align: center;
  > span {
    max-width: 168px;
    color: rgb(33, 33, 36);
    font-family: Eina01-SemiBold;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.24px;

    text-overflow: ellipsis;
    overflow: hidden;
  }

  @media (orientation: landscape) {
    height: 95px;
    min-width: calc(100vw / 7);

    > span {
      max-width: 95%;
    }
  }
`;

const CategoryListItemActive = styled.div`
  min-width: calc(100vw / 5);
  text-align: center;
  > span {
    display: inline-block;
    max-width: 195px;
    font-family: Eina01-SemiBold;
    color: rgb(33, 33, 36);
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 0.24px;

    text-overflow: ellipsis;
    overflow: hidden;
  }

  @media (orientation: landscape) {
    min-width: calc(100vw / 7);

    > span {
      max-width: 95%;
    }
  }
`;

const CategoryListItemIndent = styled.div`
  height: 136px;
  min-width: calc((100vw / 5) * 2);

  @media (orientation: landscape) {
    min-width: calc((100vw / 7) * 3);
  }
`;

const Active = styled.div`
  display: flex;
  align-items: center;
  background: rgb(255, 255, 255);
  border-radius: 68px;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14),
    0px 1px 18px 0px rgba(0, 0, 0, 0.12);
  height: 136px;
  width: calc(100vw / 5);
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  overflow-x: scroll;
  pointer-events: none;

  ::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  @media (orientation: landscape) {
    width: calc(100vw / 7);
    height: 95px;
  }
`;

interface Props {
  categoryId: string;
  setCategoryId: React.Dispatch<React.SetStateAction<string>>;
  categories: CompleteExpressQueryKiosk_company_categories[];
}

const HorizontalList = ({ categoryId, setCategoryId, categories }: Props) => {
  const { i18n } = useI18n();
  const mainListRef = useRef<HTMLDivElement>(null);
  const asideListRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(0);
  const categoryRef: any = useRef({});
  const isLandscape = useIsLandscape();

  useEffect(() => {
    // During Ref initialization the categories array may be modified (filtering), which means we may have fields that have a null value.
    // This causes errors, so we remove fields that have a null value
    for (const key in categoryRef.current) {
      if (!categoryRef.current[key]) {
        delete categoryRef.current[key];
      }
    }
  }, [categories]);

  const getSizes = (id: string) => {
    const sideIndentCount = isLandscape ? 3 : 2;
    const current = categoryRef.current[id];
    const { width } = current.getBoundingClientRect();
    const offsetLeft = current.offsetLeft;
    const itemPosition = offsetLeft - width * sideIndentCount;

    return { itemPosition, itemWidth: width };
  };

  const handleClick = (id: string) => {
    const { itemPosition } = getSizes(id);
    mainListRef.current?.scrollTo({ left: itemPosition, behavior: 'smooth' });
    setCategoryId(id);
  };

  const onScroll = () => {
    const mainScroll = mainListRef.current?.scrollLeft;
    asideListRef.current?.scrollTo({ left: mainScroll });

    clearTimeout(isScrolling.current);

    isScrolling.current = setTimeout(function() {
      autoScrollTo();
    }, 200);
  };

  const autoScrollTo = () => {
    const containerScrollLeft = mainListRef.current?.scrollLeft;

    for (const key in categoryRef.current) {
      const { itemPosition, itemWidth } = getSizes(key);

      const max = Number(containerScrollLeft) + itemWidth / 2;
      const min = Number(containerScrollLeft) - itemWidth / 2;
      if (itemPosition > min && itemPosition < max) {
        handleClick(key);
        return;
      }
    }
  };

  if (categories.length === 0) {
    return null;
  }

  return (
    <CategoriesList>
      <CategoryGradient />
      <CategoryGradient right />
      <CategoriesListInner ref={mainListRef} onScroll={onScroll}>
        <Active ref={asideListRef}>
          {categories.map(({ nameLang, id }, index) => (
            <CategoryListItemActive key={id}>
              <span>{i18n(nameLang)}</span>
            </CategoryListItemActive>
          ))}
        </Active>
        <CategoryListItemIndent />
        {categories.map(({ id, nameLang }, index) => (
          <CategoryListItem
            key={id}
            onClick={() => handleClick(id)}
            ref={(el) => (categoryRef.current[id] = el)}>
            <span>{i18n(nameLang)}</span>
          </CategoryListItem>
        ))}
        <CategoryListItemIndent />
      </CategoriesListInner>
    </CategoriesList>
  );
};

export default HorizontalList;
